import {
    ContentBlockFragment,
    MainContentBlockQueryResult,
    SectionFragment,
} from '@graphqlTypes/sitecore';

/**
 * filter by segment codes and account codes in all sections.
 * this function will directly change the content block list of each section.
 * And will remove the section when its content block list is empty
 * @param mainContent
 * @param segCode
 * @param accountCode
 */
export function filterContentBlocksInAllSections(
    mainContent: MainContentBlockQueryResult,
    segCode?: string | string[],
    accountCode?: string | string[]
) {
    if (mainContent.Item?.sections) {
        mainContent.Item.sections = mainContent.Item.sections.filter((section) => {
            filterContentBlocks(section, segCode, accountCode);
            return section?.contentBlocks.length > 0;
        });
    }
    return mainContent;
}

/**
 * filter by segment codes and account codes
 *  this function will directly change the content block list of each section.
 * @param contentBlocks
 * @param segCode
 * @param accountCode
 * @returns will return a new  array of content block
 */
export function filterContentBlocks(
    section?: SectionFragment,
    segCode?: string | string[],
    accountCode?: string | string[]
) {
    if (section) {
        const isHeroBanner = section.key === 'hero-banner';
        // if hero-banner section, show multi-selected articles
        // other sections, show all articles, but order by multi-selected article with higher priority
        const sourceBlocks = isHeroBanner
            ? section.displayArticles?.targetItems ?? []
            : section.contentBlocks;
        section.contentBlocks = sourceBlocks.filter((content) => {
            const matched = predicateContentBlock(content, segCode, accountCode);
            clearAcSegInfos(content);
            return matched;
        });
    }
}

export function OrderContentBlock(
    AllArticles?: ContentBlockFragment[],
    SelectArticles?: ContentBlockFragment[]
) {
    const selectedIds = new Set(SelectArticles?.map((item) => item.id));
    const matchedFromSelect =
        SelectArticles?.filter((select) => AllArticles?.some((all) => all.id === select.id)) ?? [];
    const remainingFromAll = AllArticles?.filter((all) => !selectedIds.has(all.id)) ?? [];
    const finalList: ContentBlockFragment[] = [...matchedFromSelect, ...remainingFromAll];
    return finalList;
}

export function predicateContentBlock(
    contentBlock:
        | Partial<
              {
                  parent: { name: string; useSimplifiedChinese?: { boolValue?: boolean } };
              } & ContentBlockFragment
          >
        | undefined,
    segCode: string | string[] | undefined,
    accountCode: string | string[] | undefined
): boolean {
    const published = isContentBlockPublished(contentBlock);
    const segMatched = () => predicateAcOrSeg(contentBlock?.segmentCodes?.targetItems, segCode);
    const acMatched = () =>
        predicateAcOrSeg(contentBlock?.accountListCodes?.targetItems, accountCode);
    const expired = isContentBlockExpired(contentBlock);
    const isPublic = () => {
        const segLength = contentBlock?.segmentCodes?.targetItems?.length ?? 0;
        const acLength = contentBlock?.accountListCodes?.targetItems?.length ?? 0;
        return segLength === 0 && acLength === 0;
    };

    const languageExisted = !!contentBlock?.pageTitle?.value;

    return languageExisted && published && !expired && (isPublic() || acMatched() || segMatched());
}

export function isContentBlockPublished(
    contentBlock:
        | Partial<
              {
                  parent: { name: string; useSimplifiedChinese?: { boolValue?: boolean } };
              } & ContentBlockFragment
          >
        | undefined
) {
    return contentBlock?.isPublished?.boolValue ?? false;
}

export function isContentBlockExpired(
    contentBlock?: Partial<
        {
            parent: { name: string; useSimplifiedChinese?: { boolValue?: boolean } };
        } & ContentBlockFragment
    >
): boolean {
    const now = Date.now();
    const startDate = contentBlock?.startDatetime?.dateValue;
    const endDate = contentBlock?.endDatetime?.dateValue;
    const isStarted = startDate && startDate > 0 && now >= startDate;
    const isEnd = endDate && endDate >= 0 && now > endDate;
    return (!isStarted || isEnd) as boolean;
}

export function isHeroBannerDate(contentBlock?: ContentBlockFragment): boolean {
    const now = Date.now();
    const startDate = contentBlock?.bannerStart?.dateValue;
    const endDate = contentBlock?.bannerEnd?.dateValue;

    if (!startDate || startDate <= 0) {
        return false;
    }

    const finalEndDate = !endDate || endDate <= 0 ? new Date('3999-12-31').getTime() : endDate;

    return now >= startDate && now <= finalEndDate;
}

export const isHeroBannerShow = (i: ContentBlockFragment[]) => {
    const data = i.filter((i) => isHeroBannerDate(i));
    return data ?? [];
};

function predicateAcOrSeg(acOrSegList?: { name: string }[], targets?: string | string[]): boolean {
    let matched = false;
    if (acOrSegList?.length) {
        if (typeof targets === 'string') {
            matched = acOrSegList.some((item) => item.name === targets);
        } else if (Array.isArray(targets)) {
            matched = targets.some((target) => {
                return acOrSegList.some((item) => item.name === target);
            });
        }
    }
    return matched;
}

function clearAcSegInfos(contentBlock: ContentBlockFragment) {
    contentBlock.segmentCodes = {};
    contentBlock.accountListCodes = {};
}

interface GraphqlMainContentDataInterface {
    results: {
        totalCount: number;
        items: Array<{
            item: {
                parent: {
                    key: string;
                    sectionTitle: { value: string };
                    // masterCategory: { category: null | any };
                    layoutType: { layout: { key: string } };
                    numberOfContent: { numberValue: number };
                };
                id: string;
                key: string;
                path: string;
                url: string;
                blockType: { value: string };
                pageTitle: { value: string };
                description: { value: string };
                heroImage: { src: string };
                heroImageSq: { src: string };
                hashTags: { tags: any[] };
                category: { categories: any[] };
                segmentCodes: { targetItems: any[] };
                accountListCodes: { targetItems: any[] };
                editorsPick: { boolValue: boolean };
                shareIcon: { boolValue: boolean };
                moreBlock: { boolValue: boolean };
                detailTitleBlock: { boolValue: boolean };
                startDatetime: { dateValue: number };
                endDatetime: { dateValue: number };
                isPublished: { boolValue: boolean };
                updateDatetime: { dateValue: number };
                eventType: {
                    targetItem: {
                        key: string;
                        eventTypeName: { value: string };
                        eventTypeIcon: { src: string };
                        eventTypeColor: { value: string };
                    };
                };
                eventDate: { dateValue: number };
                eventEndDate: { dateValue: number };
                calendarTitle: { value: string };
                calendarSubTitle: { value: string };
                calendarActionText: { value: string };
                calendarEnabled: { boolValue: boolean };
                addToCalendarEnabled: { boolValue: boolean };
                deepLink: { value: string };
                iconDisplay: { targetItem: null | any };
                iconDisplayStartDate: { dateValue: number };
                iconDisplayEndDate: { dateValue: number };
                isIconDisplay: { boolValue: boolean };
                externalSeg: { boolValue: boolean };
                externalLink: { value: string };
                externalHashId: { boolValue: boolean };
                externalAc: { boolValue: boolean };
                internalLink: { value: string };
                raceLink: { targetItem: null | any };
                videoTitle: { value: string };
                videoLink: { value: string };
                videoPoster: { src: null | string };
                videoNative: { boolValue: boolean };
                videoNativePip: { boolValue: boolean };
            };
        }>;
    };
}

const fixedOrder = [
    'hero-banner',
    'kol-section',
    'must-see-video',
    'top-headlines',
    'dont-miss-these-events',
    'offers-for-you',
    'whats-new',
    'skill-boost',
];

export const transformMainContentData: MainContentBlockQueryResult | any = (
    graphqlData: GraphqlMainContentDataInterface
) => {
    const items = graphqlData.results.items;

    const sectionsMap: { [key: string]: any } = {};

    items.forEach((item) => {
        const parentKey = item.item.parent.key;
        const parentData = item.item.parent;

        // 如果 sectionsMap 中没有该 parent.key，初始化它
        if (!sectionsMap[parentKey]) {
            sectionsMap[parentKey] = {
                key: parentKey,
                sectionTitle: parentData.sectionTitle,
                // masterCategory: parentData.masterCategory,
                layoutType: {
                    layout: {
                        key: parentData.layoutType.layout.key,
                    },
                },
                numberOfContent: {
                    numberValue: parentData.numberOfContent.numberValue,
                },
                contentBlocks: [],
            };
        }

        // 将内容块添加到相关的 section 中
        sectionsMap[parentKey].contentBlocks.push({
            id: item.item.id,
            key: item.item.key,
            path: item.item.path,
            url: item.item.url,
            blockType: item.item.blockType,
            pageTitle: item.item.pageTitle,
            description: item.item.description,
            heroImage: item.item.heroImage,
            heroImageSq: item.item.heroImageSq,
            hashTags: item.item.hashTags,
            category: item.item.category,
            segmentCodes: item.item.segmentCodes,
            accountListCodes: item.item.accountListCodes,
            editorsPick: item.item.editorsPick,
            shareIcon: item.item.shareIcon,
            moreBlock: item.item.moreBlock,
            detailTitleBlock: item.item.detailTitleBlock,
            startDatetime: item.item.startDatetime,
            endDatetime: item.item.endDatetime,
            isPublished: item.item.isPublished,
            updateDatetime: item.item.updateDatetime,
            eventType: item.item.eventType,
            eventDate: item.item.eventDate,
            eventEndDate: item.item.eventEndDate,
            calendarTitle: item.item.calendarTitle,
            calendarSubTitle: item.item.calendarSubTitle,
            calendarActionText: item.item.calendarActionText,
            calendarEnabled: item.item.calendarEnabled,
            addToCalendarEnabled: item.item.addToCalendarEnabled,
            deepLink: item.item.deepLink,
            iconDisplay: item.item.iconDisplay,
            iconDisplayStartDate: item.item.iconDisplayStartDate,
            iconDisplayEndDate: item.item.iconDisplayEndDate,
            isIconDisplay: item.item.isIconDisplay,
            externalSeg: item.item.externalSeg,
            externalLink: item.item.externalLink,
            externalHashId: item.item.externalHashId,
            externalAc: item.item.externalAc,
            internalLink: item.item.internalLink,
            raceLink: item.item.raceLink,
            videoTitle: item.item.videoTitle,
            videoLink: item.item.videoLink,
            videoPoster: item.item.videoPoster,
            videoNative: item.item.videoNative,
            videoNativePip: item.item.videoNativePip,
        });
    });

    // 将分组后的结果转为数组
    const sectionsArray = Object.values(sectionsMap);

    // 按照固定顺序排序
    const sortedSections = fixedOrder
        .map((key) => sectionsArray.find((section) => section.key === key))
        .filter((section) => section !== undefined) // 过滤掉未找到的部分
        .concat(sectionsArray.filter((section) => !fixedOrder.includes(section.key))); // 添加其他未排序的部分

    // 将分组后的结果转为数组
    return {
        Item: {
            sections: sortedSections,
        },
    };
};
